// src/services/ticketService.js
import axios from 'axios';
import { API } from '../utils/apiUrls';
import useAuth from '../hooks/useAuth';

/**
 * Support Ticket Service
 * Handles all API calls for WayuTickets support system
 */

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return {
    Authorization: token,
    'Content-Type': 'application/json',
  };
};

const getMultipartHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return {
    Authorization: token,
  };
};

// Ticket CRUD Operations
export const ticketService = {
  /**
   * Get ticket by order reference
   */
  getTicketByOrder: async (merchantId, orderReferenceNumber) => {
    const response = await axios.get(API.getTicketByOrder(), {
      params: {
        merchant_id: merchantId,
        order_reference_number: orderReferenceNumber,
      },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Create a new support ticket
   */
  createTicket: async (ticketData) => {
    const response = await axios.post(API.createTicket(), ticketData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Update ticket details
   */
  updateTicket: async (publicId, updates) => {
    const response = await axios.patch(API.updateTicket(publicId), updates, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Get single ticket by public ID
   */
  getTicket: async (publicId, requesterId) => {
    const response = await axios.get(API.getTicket(publicId), {
      params: { requesterId },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * List tickets with filters
   */
  listTickets: async (filters = {}) => {
    const params = {
      tenantId: filters.tenantId || 'wayuvega_b2c',
      requesterId: filters.requesterId,
      ...(filters.status && { status: filters.status }),
      ...(filters.category && { category: filters.category }),
      ...(filters.createdAfter && { createdAfter: filters.createdAfter }),
      ...(filters.createdBefore && { createdBefore: filters.createdBefore }),
      ...(filters.slaBreached !== undefined && { slaBreached: filters.slaBreached }),
      limit: filters.limit || 50,
      offset: filters.offset || 0,
    };
    const response = await axios.get(API.listTickets(), {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Close a ticket
   */
  closeTicket: async (publicId, closureData) => {
    const response = await axios.post(API.closeTicket(publicId), closureData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Reopen a closed ticket
   */
  reopenTicket: async (publicId, reopenData) => {
    const response = await axios.post(API.reopenTicket(publicId), reopenData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Upload attachments to ticket
   */
  uploadTicketAttachments: async (publicId, files, actorData) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('actor_type', actorData.actor_type);
    formData.append('actor_id', actorData.actor_id);
    if (actorData.actor_name) formData.append('actor_name', actorData.actor_name);
    if (actorData.actor_email) formData.append('actor_email', actorData.actor_email);

    const response = await axios.post(API.uploadTicketAttachments(publicId), formData, {
      headers: getMultipartHeaders(),
    });
    return response.data;
  },

  /**
   * Delete ticket attachment
   */
  deleteTicketAttachment: async (publicId, attachmentId) => {
    const response = await axios.delete(API.deleteTicketAttachment(publicId, attachmentId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Get download URL for ticket attachment
   */
  downloadTicketAttachment: async (publicId, attachmentId) => {
    const response = await axios.get(API.downloadTicketAttachment(publicId, attachmentId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * List all messages for a ticket
   */
  listTicketMessages: async (publicId, limit = 50, offset = 0) => {
    const response = await axios.get(API.listTicketMessages(publicId), {
      params: { limit, offset },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Add a message/comment to ticket
   */
  addTicketMessage: async (publicId, messageData, files = []) => {
    const formData = new FormData();
    formData.append('message', messageData.message);
    if (messageData.body_format) formData.append('body_format', messageData.body_format);
    formData.append('actor_type', messageData.actor_type);
    formData.append('actor_id', messageData.actor_id);
    if (messageData.actor_name) formData.append('actor_name', messageData.actor_name);
    if (messageData.actor_email) formData.append('actor_email', messageData.actor_email);
    
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await axios.post(API.addTicketMessage(publicId), formData, {
      headers: getMultipartHeaders(),
    });
    return response.data;
  },

  /**
   * Update a message
   * updateData should contain: { message: "..." }
   */
  updateTicketMessage: async (publicId, sequenceNumber, updateData) => {
    const response = await axios.patch(
      API.updateTicketMessage(publicId, sequenceNumber),
      { message: updateData.message },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Delete a message
   */
  deleteTicketMessage: async (publicId, sequenceNumber, deletedBy = null) => {
    const response = await axios.delete(API.deleteTicketMessage(publicId, sequenceNumber), {
      data: deletedBy ? { deleted_by: deletedBy } : undefined,
      headers: getAuthHeaders(),
    });
    return response.status === 204;
  },

  /**
   * Add attachments to existing message
   */
  addMessageAttachment: async (publicId, sequenceNumber, files, actorData) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('actor_type', actorData.actor_type);
    formData.append('actor_id', actorData.actor_id);
    if (actorData.actor_name) formData.append('actor_name', actorData.actor_name);
    if (actorData.actor_email) formData.append('actor_email', actorData.actor_email);

    const response = await axios.post(
      API.addMessageAttachment(publicId, sequenceNumber),
      formData,
      {
        headers: getMultipartHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Delete message attachment
   */
  deleteMessageAttachment: async (publicId, sequenceNumber, attachmentId, removedBy = null) => {
    const response = await axios.delete(
      API.deleteMessageAttachment(publicId, sequenceNumber, attachmentId),
      {
        data: removedBy ? { removed_by: removedBy } : undefined,
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get download URL for message attachment
   */
  downloadMessageAttachment: async (publicId, sequenceNumber, attachmentId) => {
    const response = await axios.get(
      API.downloadMessageAttachment(publicId, sequenceNumber, attachmentId),
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get ticket activity timeline
   */
  getTicketTimeline: async (publicId, limit = 50, offset = 0) => {
    const response = await axios.get(API.getTicketTimeline(publicId), {
      params: { limit, offset },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Get dashboard summary statistics
   */
  getDashboardSummary: async (tenantId, requesterId) => {
    const response = await axios.get(API.getTicketDashboardSummary(), {
      params: { tenantId, requesterId },
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

export default ticketService;

